import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTheme } from '../hooks/useTheme'
import { Button } from '../components/ui'
import { TourIcon, type TourIconId } from './tourVisuals'

interface Props {
  target: string
  title: string
  body: string
  stepIndex: number
  stepCount: number
  iconId: TourIconId
  skipLabel: string
  nextLabel: string
  doneLabel: string
  /** Force tip position; default auto. */
  tipPlacement?: TipPlacement
  /** Skip quickly when the target is not in the DOM. */
  optional?: boolean
  onNext: () => void
  onSkip: () => void
}

type Rect = { top: number; left: number; width: number; height: number }

const PAD = 8
const DIM = 'rgba(0,0,0,0.48)'
/** Approx tip height used before the tip is measured. */
const TIP_ESTIMATE = 128
const NAV_CLEARANCE = 88
const TIP_GAP = 10
const TOP_SAFE = 56

type TipPlacement = 'bottom' | 'above'

function tipReserve(tipHeight: number): number {
  return NAV_CLEARANCE + tipHeight + TIP_GAP
}

/**
 * Prefer room below the spotlight for the tip. Only place above when the tip
 * fully fits above without clipping the top of the viewport.
 */
function resolveTipPlacement(
  hole: { top: number; height: number } | null,
  tipHeight: number,
  preferred?: TipPlacement
): TipPlacement {
  if (preferred) return preferred
  if (!hole) return 'bottom'
  const vh = window.innerHeight
  const holeBottom = hole.top + hole.height
  const roomBelow = vh - holeBottom - NAV_CLEARANCE
  if (roomBelow >= tipHeight + TIP_GAP) return 'bottom'

  const roomAbove = hole.top - TOP_SAFE
  if (roomAbove >= tipHeight + TIP_GAP) return 'above'

  // Tall target filling the screen — keep tip at bottom (scrolled to leave space)
  return 'bottom'
}

/** Scroll so the target leaves a band for the tip under (or above) it. */
function scrollTargetForTip(el: HTMLElement, tipHeight: number, preferred?: TipPlacement) {
  const rect = el.getBoundingClientRect()
  const vh = window.innerHeight

  if (preferred === 'above') {
    // Pin target toward the lower band so the tip fits fully above it
    const minTop = TOP_SAFE + tipHeight + TIP_GAP
    const desiredTop = Math.max(minTop, vh - NAV_CLEARANCE - rect.height - 12)
    window.scrollBy(0, rect.top - desiredTop)
    return
  }

  // Default / bottom: keep the whole target above the tip + bottom nav
  const reserve = tipReserve(tipHeight)
  const available = Math.max(120, vh - reserve)
  const maxHoleBottom = vh - reserve
  const desiredTop = TOP_SAFE + Math.max(0, Math.min(24, (available - rect.height) / 2))

  if (rect.height <= available) {
    if (rect.bottom > maxHoleBottom - 4 || rect.top < TOP_SAFE - 4 || Math.abs(rect.top - desiredTop) > 12) {
      window.scrollBy(0, rect.top - desiredTop)
    }
    return
  }

  // Tall target: pin its top near the viewport top so tip sits in the lower band
  if (rect.top < TOP_SAFE || rect.bottom > maxHoleBottom) {
    window.scrollBy(0, rect.top - TOP_SAFE)
  }
}

export default function CoachmarkOverlay({
  target,
  title,
  body,
  stepIndex,
  stepCount,
  iconId,
  skipLabel,
  nextLabel,
  doneLabel,
  tipPlacement: preferredPlacement,
  optional = false,
  onNext,
  onSkip,
}: Props) {
  const theme = useTheme()
  const tipRef = useRef<HTMLDivElement>(null)
  const [rect, setRect] = useState<Rect | null>(null)
  const [tipHeight, setTipHeight] = useState(TIP_ESTIMATE)
  const [sheetIn, setSheetIn] = useState(false)
  const sheetKey = `${target}-${stepIndex}`
  const [seenSheetKey, setSeenSheetKey] = useState(sheetKey)
  if (seenSheetKey !== sheetKey) {
    setSeenSheetKey(sheetKey)
    setSheetIn(false)
  }
  const isLast = stepIndex >= stepCount - 1

  const readTargetRect = useCallback((): Rect | null => {
    const el = document.querySelector(`[data-tour="${target}"]`) as HTMLElement | null
    if (!el) return null
    const r = el.getBoundingClientRect()
    return {
      top: r.top,
      left: r.left,
      width: r.width,
      height: r.height,
    }
  }, [target])

  // Position target, freeze spotlight, then lock page scroll for this step
  useLayoutEffect(() => {
    const html = document.documentElement
    const body = document.body
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      bodyOverscroll: body.style.overscrollBehavior,
      bodyTouchAction: body.style.touchAction,
    }

    function unlockForScroll() {
      const lockedTop = body.style.top
      const y = lockedTop ? -parseInt(lockedTop, 10) || 0 : window.scrollY
      body.style.position = ''
      body.style.top = ''
      body.style.width = ''
      body.style.overflow = ''
      body.style.touchAction = ''
      html.style.overflow = ''
      window.scrollTo(0, y)
      return y
    }

    function lockAtCurrentScroll() {
      const scrollY = window.scrollY
      html.style.overflow = 'hidden'
      body.style.overflow = 'hidden'
      body.style.position = 'fixed'
      body.style.top = `-${scrollY}px`
      body.style.width = '100%'
      body.style.overscrollBehavior = 'none'
      body.style.touchAction = 'none'
      return scrollY
    }

    function measuredTipHeight(): number {
      return tipRef.current?.getBoundingClientRect().height || TIP_ESTIMATE
    }

    function positionAndLock() {
      unlockForScroll()
      const el = document.querySelector(`[data-tour="${target}"]`) as HTMLElement | null
      if (el) {
        scrollTargetForTip(el, measuredTipHeight(), preferredPlacement)
      }
      lockAtCurrentScroll()
      const nextRect = readTargetRect()
      setRect(nextRect)
      const h = measuredTipHeight()
      if (h > 0) setTipHeight(h)
    }

    const hadTarget = !!document.querySelector(`[data-tour="${target}"]`)
    positionAndLock()

    const blockScroll = (e: Event) => {
      e.preventDefault()
    }

    document.addEventListener('wheel', blockScroll, { passive: false })
    document.addEventListener('touchmove', blockScroll, { passive: false })

    let findPoll: number | undefined
    let missingTimer: number | undefined
    if (!hadTarget) {
      let attempts = 0
      const maxAttempts = optional ? 4 : 40
      findPoll = window.setInterval(() => {
        attempts += 1
        const el = document.querySelector(`[data-tour="${target}"]`)
        if (el) {
          positionAndLock()
          window.clearInterval(findPoll)
        } else if (attempts > maxAttempts) {
          window.clearInterval(findPoll)
          if (optional) {
            missingTimer = window.setTimeout(() => onNext(), 0)
          }
        }
      }, 100)
    }

    // Home widgets load async and shift Quick Access — re-lock while the target drifts.
    let last = readTargetRect()
    let settleAttempts = 0
    const settlePoll = window.setInterval(() => {
      settleAttempts += 1
      const el = document.querySelector(`[data-tour="${target}"]`) as HTMLElement | null
      if (!el) {
        if (settleAttempts > 30) window.clearInterval(settlePoll)
        return
      }
      const next = {
        top: el.getBoundingClientRect().top,
        left: el.getBoundingClientRect().left,
        width: el.getBoundingClientRect().width,
        height: el.getBoundingClientRect().height,
      }
      const drifted =
        !last ||
        Math.abs(next.top - last.top) > 6 ||
        Math.abs(next.height - last.height) > 6
      if (drifted) {
        positionAndLock()
        last = readTargetRect()
      }
      if (settleAttempts > 30) window.clearInterval(settlePoll)
    }, 100)

    const onResize = () => {
      positionAndLock()
    }
    window.addEventListener('resize', onResize)

    return () => {
      if (findPoll !== undefined) window.clearInterval(findPoll)
      if (missingTimer !== undefined) window.clearTimeout(missingTimer)
      window.clearInterval(settlePoll)
      window.removeEventListener('resize', onResize)
      document.removeEventListener('wheel', blockScroll)
      document.removeEventListener('touchmove', blockScroll)
      const lockedTop = body.style.top
      const y = lockedTop ? -parseInt(lockedTop, 10) || 0 : 0
      html.style.overflow = prev.htmlOverflow
      body.style.overflow = prev.bodyOverflow
      body.style.position = prev.bodyPosition
      body.style.top = prev.bodyTop
      body.style.width = prev.bodyWidth
      body.style.overscrollBehavior = prev.bodyOverscroll
      body.style.touchAction = prev.bodyTouchAction
      window.scrollTo(0, y)
    }
  }, [target, preferredPlacement, optional, onNext, readTargetRect])

  // Re-measure tip after content paints; if height grew, re-scroll once
  useLayoutEffect(() => {
    const tip = tipRef.current
    if (!tip) return
    const h = tip.getBoundingClientRect().height
    if (h > 0 && Math.abs(h - tipHeight) > 2) {
      setTipHeight(h)
    }
  }, [title, body, stepIndex, tipHeight])

  useEffect(() => {
    const enter = window.requestAnimationFrame(() => setSheetIn(true))
    return () => window.cancelAnimationFrame(enter)
  }, [sheetKey])

  const hole = rect
    ? {
      top: Math.max(0, rect.top - PAD),
      left: Math.max(0, rect.left - PAD),
      width: rect.width + PAD * 2,
      height:
        preferredPlacement === 'above'
          ? rect.height + PAD * 2
          : Math.min(
            rect.height + PAD * 2,
            Math.max(
              80,
              window.innerHeight - Math.max(0, rect.top - PAD) - tipReserve(tipHeight)
            )
          ),
    }
    : null

  const placement = resolveTipPlacement(hole, tipHeight, preferredPlacement)
  const tipPositionStyle =
    placement === 'above' && hole
      ? {
        position: 'fixed' as const,
        left: 16,
        right: 16,
        top: Math.max(TOP_SAFE, hole.top - tipHeight - TIP_GAP),
        bottom: 'auto' as const,
      }
      : {
        position: 'fixed' as const,
        left: 16,
        right: 16,
        bottom: 'calc(5.5rem + env(safe-area-inset-bottom, 0px))' as const,
        top: 'auto' as const,
      }

  return (
    <div
      className="fixed inset-0 z-[85] overscroll-none"
      style={{ touchAction: 'none' }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {hole ? (
        <>
          <div className="absolute inset-x-0 top-0" style={{ height: hole.top, backgroundColor: DIM }} />
          <div
            className="absolute left-0"
            style={{
              top: hole.top,
              width: hole.left,
              height: hole.height,
              backgroundColor: DIM,
            }}
          />
          <div
            className="absolute right-0"
            style={{
              top: hole.top,
              left: hole.left + hole.width,
              height: hole.height,
              backgroundColor: DIM,
            }}
          />
          <div
            className="absolute inset-x-0 bottom-0"
            style={{
              top: hole.top + hole.height,
              backgroundColor: DIM,
            }}
          />
          <div
            className="absolute rounded-2xl pointer-events-none"
            style={{
              top: hole.top,
              left: hole.left,
              width: hole.width,
              height: hole.height,
              boxShadow: `0 0 0 2px ${theme.buttonColor}, 0 0 0 8px ${theme.buttonColor}22`,
            }}
          />
        </>
      ) : (
        <div className="absolute inset-0" style={{ backgroundColor: DIM }} />
      )}

      <div
        ref={tipRef}
        data-tour-tip
        className="rounded-xl px-3 py-2.5 transition-all duration-200 ease-out"
        style={{
          ...tipPositionStyle,
          touchAction: 'manipulation',
          backgroundColor: theme.modal.bg,
          border: `1px solid ${theme.cardBorder}`,
          boxShadow: theme.isDark
            ? '0 4px 20px rgba(0,0,0,0.35)'
            : '0 4px 16px rgba(0,0,0,0.08)',
          zIndex: 1,
          maxHeight: `calc(100vh - ${TOP_SAFE + NAV_CLEARANCE}px)`,
          overflowY: 'auto',
          opacity: sheetIn ? 1 : 0,
          transform: sheetIn
            ? 'translateY(0)'
            : placement === 'above'
              ? 'translateY(-8px)'
              : 'translateY(8px)',
        }}
      >
        <div className="flex items-start gap-2.5 mb-2">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
            style={{
              backgroundColor: `${theme.buttonColor}18`,
              color: theme.buttonColor,
            }}
            aria-hidden
          >
            <TourIcon id={isLast ? 'done' : iconId} size={15} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2 mb-0.5">
              <h3 className="text-sm font-bold leading-snug" style={{ color: theme.text.primary }}>
                {title}
              </h3>
              <span
                className="text-[10px] font-semibold tabular-nums shrink-0"
                style={{ color: theme.text.muted }}
              >
                {stepIndex + 1}/{stepCount}
              </span>
            </div>
            <p className="text-xs leading-snug" style={{ color: theme.text.secondary }}>
              {body}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onSkip}
            className="min-h-9 px-2 text-xs font-semibold rounded-lg active:opacity-70 transition-opacity"
            style={{ color: theme.text.muted }}
          >
            {skipLabel}
          </button>
          <Button type="button" variant="primary" size="sm" className="flex-1" onClick={onNext}>
            {isLast ? doneLabel : nextLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
